import type { ReactNode } from 'react';

import { formatMoney } from '@/utils/format';

export interface CurrencyAmount {
  currency: string;
  amountMinor: number;
}

export interface ListMoneyTotals {
  amountMinor?: number;
  currency?: string;
  count: number;
  byCurrency?: CurrencyAmount[];
}

export const hasMixedCurrencyTotals = (
  totals: ListMoneyTotals | undefined,
): totals is ListMoneyTotals & { byCurrency: CurrencyAmount[] } =>
  Boolean(totals?.byCurrency && totals.byCurrency.length > 0);

export const formatMoneyTotalsSummary = (
  totals: ListMoneyTotals | undefined,
  fallbackCurrency: string,
): string => {
  if (!totals) return '…';
  if (hasMixedCurrencyTotals(totals)) {
    return totals.byCurrency
      .map((item) => formatMoney(item.amountMinor, item.currency, { showSymbol: true }))
      .join(' · ');
  }
  if (totals.amountMinor === undefined) return '…';
  return formatMoney(totals.amountMinor, totals.currency ?? fallbackCurrency, {
    showSymbol: true,
  });
};

export const dataTableMoneyColumnTotals = (
  columnId: string,
  totals: ListMoneyTotals | undefined,
  fallbackCurrency: string,
): Record<string, { amount: number; currency: string } | Array<{ amount: number; currency: string }>> | undefined => {
  if (!totals) return undefined;
  if (hasMixedCurrencyTotals(totals)) {
    return {
      [columnId]: totals.byCurrency.map((item) => ({
        amount: item.amountMinor,
        currency: item.currency,
      })),
    };
  }
  if (totals.amountMinor === undefined) return undefined;
  return {
    [columnId]: {
      amount: totals.amountMinor,
      currency: totals.currency ?? fallbackCurrency,
    },
  };
};

export const aggregateByCurrency = <T,>(
  rows: T[],
  getAmount: (row: T) => number,
  getCurrency: (row: T) => string,
): CurrencyAmount[] => {
  const map = new Map<string, number>();
  for (const row of rows) {
    const currency = getCurrency(row);
    map.set(currency, (map.get(currency) ?? 0) + getAmount(row));
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, amountMinor]) => ({ currency, amountMinor }));
};

/** Hides zero amounts for currencies other than the church base currency. */
export const visibleCurrencyAmounts = (
  items: CurrencyAmount[],
  baseCurrency: string,
): CurrencyAmount[] =>
  items.filter(
    (item) => item.currency === baseCurrency || item.amountMinor !== 0,
  );

export const formatCurrencyAmounts = (
  items: CurrencyAmount[],
  format: (amount: number, currency: string) => string = (amount, currency) =>
    formatMoney(amount, currency, { showSymbol: true }),
): ReactNode => {
  if (items.length === 0) return '—';
  if (items.length === 1) return format(items[0]!.amountMinor, items[0]!.currency);
  return items.map((item) => format(item.amountMinor, item.currency)).join(' · ');
};

export const formatCurrencyAmountList = (
  items: CurrencyAmount[],
  baseCurrency?: string,
): string => {
  const visible = baseCurrency ? visibleCurrencyAmounts(items, baseCurrency) : items;
  if (visible.length === 0) return '…';
  return visible
    .map((item) => formatMoney(item.amountMinor, item.currency, { showSymbol: true }))
    .join(' · ');
};

export const occurrenceMoneyTotals = (
  rows: Array<{ currency: string; amountMinor: number; count?: number }>,
): { amounts: CurrencyAmount[]; count: number } => ({
  amounts: aggregateByCurrency(rows, (row) => row.amountMinor, (row) => row.currency),
  count: rows.reduce((sum, row) => sum + (row.count ?? 0), 0),
});

export interface InsightsMoneyData {
  amountMinor: number;
  currency: string;
  count: number;
  byCurrency?: CurrencyAmount[];
}

export const insightsHasMultipleCurrencies = (
  data: Pick<InsightsMoneyData, 'byCurrency'> | undefined,
): boolean => Boolean(data?.byCurrency && data.byCurrency.length > 1);

export const formatInsightsMoneySummary = (
  data: InsightsMoneyData | undefined,
  fallbackCurrency: string,
): string => {
  if (!data) return '…';
  if (insightsHasMultipleCurrencies(data)) {
    return data.byCurrency!
      .map((item) => formatMoney(item.amountMinor, item.currency, { showSymbol: true }))
      .join(' · ');
  }
  return formatMoney(data.amountMinor, data.currency ?? fallbackCurrency, { showSymbol: true });
};

export type OccurrenceTotals =
  | { amountMinor: number; count: number; currency: string }
  | { byCurrency: CurrencyAmount[]; count: number };

export const occurrenceTotalsAmounts = (totals: OccurrenceTotals | undefined): CurrencyAmount[] => {
  if (!totals) return [];
  if ('byCurrency' in totals) return totals.byCurrency;
  return [{ currency: totals.currency, amountMinor: totals.amountMinor }];
};

export const formatOccurrenceTotalsSummary = (
  totals: OccurrenceTotals | undefined,
): string => {
  const amounts = occurrenceTotalsAmounts(totals);
  if (amounts.length === 0) return '…';
  if (amounts.length === 1) {
    return formatMoney(amounts[0]!.amountMinor, amounts[0]!.currency, { showSymbol: true });
  }
  return amounts
    .map((item) => formatMoney(item.amountMinor, item.currency, { showSymbol: true }))
    .join(' · ');
};
